import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import { FiX, FiExternalLink, FiBookOpen } from 'react-icons/fi';
import { getTeamSops } from '../api/sopApi';
import { sopAudienceLabel } from '../constants/sopAudiences';

export default function TeamSopViewDialog({ open, onClose }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['team-sops'],
    queryFn: getTeamSops,
    enabled: open,
    staleTime: 30_000,
  });

  const grouped = useMemo(() => {
    const items = data?.items || [];
    const m = new Map();
    items.forEach((item) => {
      const key = String(item.audience_role || 'all').toLowerCase();
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(item);
    });
    const order = ['all', 'developer', 'tester', 'designer', 'devops', 'analyst', 'product', 'lead', 'manager'];
    const keys = [...m.keys()].sort((a, b) => {
      const ia = order.indexOf(a);
      const ib = order.indexOf(b);
      if (ia >= 0 && ib >= 0) return ia - ib;
      if (ia >= 0) return -1;
      if (ib >= 0) return 1;
      return a.localeCompare(b);
    });
    return keys.map((k) => ({ key: k, label: sopAudienceLabel(k), items: m.get(k) }));
  }, [data?.items]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800 }}>
          <FiBookOpen size={20} color="#1272B3" />
          Documents
        </span>
        <IconButton onClick={onClose} aria-label="close" size="small">
          <FiX />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <CircularProgress size={32} />
          </div>
        ) : isError ? (
          <p style={{ color: '#b91c1c', fontSize: '0.9rem' }}>
            {error?.response?.data?.message || 'Failed to load documents.'}
          </p>
        ) : grouped.length === 0 ? (
          <p style={{ color: '#64748b', fontSize: '0.9rem', margin: 0 }}>
            No documents have been published yet. Your manager or admin can add links from Team Activity.
          </p>
        ) : (
          grouped.map((section) => (
            <div key={section.key} style={{ marginBottom: '1.25rem' }}>
              <h4
                style={{
                  margin: '0 0 0.5rem',
                  fontSize: '0.82rem',
                  fontWeight: 800,
                  color: '#475569',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                {section.label}
              </h4>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {section.items.map((doc) => (
                  <li
                    key={doc.id}
                    style={{
                      marginBottom: 8,
                      padding: '0.65rem 0.75rem',
                      background: '#f8fafc',
                      borderRadius: 8,
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    <a
                      href={doc.document_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                        color: '#1272B3',
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        textDecoration: 'none',
                      }}
                    >
                      <span>{doc.title}</span>
                      <FiExternalLink size={14} style={{ flexShrink: 0 }} />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <button
          type="button"
          onClick={onClose}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: 8,
            border: '1px solid #e2e8f0',
            background: '#fff',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Close
        </button>
      </DialogActions>
    </Dialog>
  );
}
